import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { catsApi, feedingsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';

const CreateFeedingPage = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [previewImages, setPreviewImages] = useState([]);
  const [photoUploadStatus, setPhotoUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [catPhotos, setCatPhotos] = useState([]);
  const [currentCatPhotoIndex, setCurrentCatPhotoIndex] = useState(0);
  
  // Fetch cat details
  useEffect(() => {
    const fetchCat = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const catData = await catsApi.getCatById(catId);
        setCat(catData);
        console.log('Cat details for feeding:', catData);
        
        // Process cat photos from the response
        if (catData.catPhotos && catData.catPhotos.length > 0) {
          const photoUrls = catData.catPhotos.map(photo => photo.path);
          setCatPhotos(photoUrls);
          console.log('Set cat photos from catPhotos array:', photoUrls);
        } else if (catData.mainPhoto) {
          setCatPhotos([catData.mainPhoto]);
          console.log('Using mainPhoto as fallback');
        } else {
          setCatPhotos(['/cat-placeholder.png']);
          console.log('No photos found, using default placeholder');
        }
      } catch (error) {
        console.error('Error fetching cat details:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    if (catId) {
      fetchCat();
    }
  }, [catId]);
  
  // Handle photo selection
  const handlePhotoChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file size and type
    const validFiles = [];
    const invalidFiles = [];
    
    selectedFiles.forEach(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (file too large, max 5MB)`);
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        invalidFiles.push(`${file.name} (not an image file)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    // Check total number of photos
    if (photos.length + validFiles.length > 5) {
      alert('You can upload a maximum of 5 photos per feeding.');
      return;
    }
    
    if (invalidFiles.length > 0) {
      alert(`Some files couldn't be added:\n${invalidFiles.join('\n')}`);
    }
    
    if (validFiles.length > 0) {
      setPhotos([...photos, ...validFiles]);
      
      // Create preview URLs
      const newPreviewImages = validFiles.map(file => URL.createObjectURL(file));
      setPreviewImages([...previewImages, ...newPreviewImages]);
      
      setPhotoUploadStatus(`${validFiles.length} photo${validFiles.length > 1 ? 's' : ''} added successfully.`);
      
      // Clear upload status after 3 seconds
      setTimeout(() => {
        setPhotoUploadStatus('');
      }, 3000);
    }
  };
  
  // Remove a photo
  const removePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
    
    const newPreviewImages = [...previewImages];
    URL.revokeObjectURL(newPreviewImages[index]); // Clean up URL object
    newPreviewImages.splice(index, 1);
    setPreviewImages(newPreviewImages);
  };
  
  // Navigate to previous cat photo
  const prevCatPhoto = (e) => {
    e.preventDefault();
    if (catPhotos.length <= 1) return;
    setCurrentCatPhotoIndex((prevIndex) => 
      (prevIndex - 1 + catPhotos.length) % catPhotos.length
    );
  };
  
  // Navigate to next cat photo
  const nextCatPhoto = (e) => {
    e.preventDefault();
    if (catPhotos.length <= 1) return;
    setCurrentCatPhotoIndex((prevIndex) => 
      (prevIndex + 1) % catPhotos.length
    );
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (photos.length === 0) {
      alert('Please add at least one photo of the feeding.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Create FormData for file upload
      const formData = new FormData();
      // catId is passed in the URL path parameter, so we don't need to include it in FormData
      formData.append('notes', notes);
      
      // Append photos as generic files without a field name
      // This matches the server's expectation with @RestForm(FileUpload.ALL)
      photos.forEach((photo) => {
        formData.append('files', photo);
      });
      
      // Set up upload progress monitoring
      const uploadWithProgress = async () => {
        return new Promise((resolve, reject) => {
          // Mock upload progress for demo purposes
          // In a real app, you'd use XMLHttpRequest's progress event
          let progress = 0;
          const interval = setInterval(() => {
            progress += 10;
            setUploadProgress(progress);
            
            if (progress >= 100) {
              clearInterval(interval);
              resolve();
            }
          }, 200);
          
          // Submit feeding (in a real app, this would be inside the XHR's onload)
          feedingsApi.createFeeding(formData, catId)
            .then(resolve)
            .catch(error => {
              clearInterval(interval);
              reject(error);
            });
        });
      };
      
      // Start the upload process
      await uploadWithProgress();
      
      // Navigate to feeding history
      navigate('/feeder/feedings', { 
        state: { success: true, message: `Successfully recorded feeding for ${cat?.name}` } 
      });
    } catch (error) {
      console.error('Error submitting feeding:', error);
      setError(handleApiError(error));
      setSubmitting(false);
      setUploadProgress(0);
    }
  };
  
  // Function to handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      // Use the same file handling logic
      handlePhotoChange({ target: { files: filesArray } });
    }
  };
  
  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previewImages.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewImages]);
  
  return (
    <div className="create-feeding-page">
      <div className="page-header">
        <h1 className="page-title">Record Feeding</h1>
        <p className="page-description">
          Document a feeding with photos and details
        </p>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {!loading && !error && cat && (
        <div className="feeding-form-container">
          <div className="cat-info-card">
            {/* Cat Photo Carousel */}
            <div className="cat-photos-carousel">
              <div className="main-photo">
                <img 
                  src={catPhotos[currentCatPhotoIndex] || '/cat-placeholder.png'} 
                  alt={cat.name} 
                  className="cat-photo"
                  onError={(e) => {
                    console.log('Image failed to load, using fallback');
                    e.target.src = '/cat-placeholder.png';
                  }}
                />
                
                {catPhotos.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="photo-nav-button prev-button"
                      onClick={prevCatPhoto}
                      aria-label="Previous photo"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="photo-nav-button next-button"
                      onClick={nextCatPhoto}
                      aria-label="Next photo"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
              
              {catPhotos.length > 1 && (
                <div className="photo-thumbnails">
                  {catPhotos.map((photo, index) => (
                    <div 
                      key={index}
                      className={`photo-thumbnail ${index === currentCatPhotoIndex ? 'active' : ''}`}
                      onClick={() => setCurrentCatPhotoIndex(index)}
                    >
                      <img 
                        src={photo} 
                        alt={`${cat.name} - photo ${index + 1}`}
                        onError={(e) => {
                          e.target.src = '/cat-placeholder.png';
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="cat-details">
              <h2 className="cat-name">{cat.name}</h2>
              <p className="cat-description">{cat.description}</p>
              {cat.pendingRequests > 0 && (
                <div className="pending-requests-badge">
                  {cat.pendingRequests} pending feeding requests
                </div>
              )}
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="feeding-form">
            <div className="form-section">
              <h3 className="section-title">Feeding Details</h3>
              
              <div className="form-group">
                <label htmlFor="notes">Notes (Optional)</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional details about the feeding..."
                  rows={4}
                  disabled={submitting}
                />
              </div>
            </div>
            
            <div className="form-section">
              <h3 className="section-title">Photos</h3>
              <p className="section-description">
                Add photos of the feeding (required, max 5 photos)
              </p>
              
              <div 
                className="photo-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  disabled={submitting}
                />
                
                {photoUploadStatus && (
                  <div className="upload-status">
                    {photoUploadStatus}
                  </div>
                )}
                
                <div className="photo-previews">
                  {previewImages.map((previewUrl, index) => (
                    <div key={index} className="photo-preview">
                      <img src={previewUrl} alt={`Preview ${index + 1}`} />
                      <button 
                        type="button"
                        className="remove-photo-btn"
                        onClick={() => removePhoto(index)}
                        disabled={submitting}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {previewImages.length < 5 && (
                    <div 
                      className="add-photo-btn"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <div className="add-icon">+</div>
                      <span>Add Photo</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {submitting && (
              <div className="upload-progress">
                <div className="progress-label">Uploading photos: {uploadProgress}%</div>
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="form-actions">
              <button 
                type="button" 
                className="button secondary-button"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </button>
              
              <button 
                type="submit" 
                className="button primary-button"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Record Feeding'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CreateFeedingPage; 